import { PASSWORD_MIN_LENGTH, passwordSchema } from "@cafegestor/shared";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { useAuth } from "@/app/auth";
import { useMe } from "@/app/me";
import { Alert, Button, Card, Field, Input, PageTitle } from "@/components/ui";
import { apiFetch, errorMessage } from "@/lib/api";
import { supabase } from "@/lib/supabase";

/**
 * Troca da própria senha pela API.
 *
 * - `forced`: senha inicial ou redefinida pelo administrador. A tela ocupa o sistema todo até a
 *   troca (a API também recusa o resto) e não pede a senha atual.
 * - fora disso (Minha senha): pede a senha atual.
 *
 * A troca encerra TODAS as sessões anteriores (inclusive esta): a tela entra de novo com a
 * senha nova, sem a pessoa precisar digitar tudo outra vez.
 */
export function ChangePasswordPage({ forced }: { forced: boolean }) {
  const { data: me } = useMe();
  const { signOut } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const valid = passwordSchema.safeParse(password);
    if (!valid.success) return setError(valid.error.issues[0]?.message ?? "Senha inválida.");
    if (password !== confirm) return setError("As senhas não conferem.");
    if (!forced && !current) return setError("Informe a sua senha atual.");
    if (!me || !supabase) return;
    setBusy(true);
    try {
      await apiFetch("/me/senha", {
        method: "POST",
        withCompany: false,
        body: forced
          ? { newPassword: password }
          : { newPassword: password, currentPassword: current },
      });
    } catch (err) {
      setBusy(false);
      return setError(errorMessage(err));
    }
    // as sessões anteriores foram encerradas: entra de novo com a senha nova
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email: me.user.email,
      password,
    });
    setBusy(false);
    if (signInError) {
      await signOut();
      await navigate({ to: "/login" });
      return;
    }
    setPassword("");
    setConfirm("");
    setCurrent("");
    await queryClient.invalidateQueries({ queryKey: ["me"] });
    setDone(true);
  }

  const form = (
    <form onSubmit={submit} className="space-y-4">
      {error && <Alert tone="error">{error}</Alert>}
      {done && !forced && (
        <Alert tone="ok">Senha alterada. As outras sessões foram encerradas.</Alert>
      )}
      {!forced && (
        <Field label="Senha atual">
          <Input
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
        </Field>
      )}
      <Field label="Nova senha" hint={`Mínimo de ${PASSWORD_MIN_LENGTH} caracteres.`}>
        <Input
          type="password"
          autoComplete="new-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </Field>
      <Field label="Confirme a nova senha">
        <Input
          type="password"
          autoComplete="new-password"
          required
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />
      </Field>
      <Button type="submit" className="w-full" disabled={busy}>
        {busy ? "Salvando…" : "Salvar nova senha"}
      </Button>
    </form>
  );

  if (!forced) {
    return (
      <div className="max-w-md space-y-6">
        <PageTitle title="Minha senha" description="Troque a sua senha de acesso." />
        <Card>{form}</Card>
      </div>
    );
  }

  const login = me?.user.login;
  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/50 px-4">
      <div className="w-full max-w-sm space-y-4 rounded-lg border bg-card p-6 shadow-sm">
        <div>
          <h1 className="text-xl font-semibold">Crie a sua senha</h1>
          <p className="text-sm text-muted-foreground">
            {login ? `Usuário ${login.username} (empresa ${login.loginCode}). ` : ""}A senha que
            você usou foi definida pelo administrador. Antes de continuar, crie uma senha só sua.
          </p>
        </div>
        {form}
        <button
          type="button"
          onClick={async () => {
            await signOut();
            await navigate({ to: "/login" });
          }}
          className="w-full text-sm text-muted-foreground underline-offset-4 hover:underline"
        >
          Sair
        </button>
      </div>
    </div>
  );
}
